import type {
    StandalonePackage,
    StandaloneRecommendation,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { has, isEmpty } from "lodash";

import type { InsightPackagesFilterSelections } from "../types/filters/insightPackages";
import { InsightPackagesFilterField } from "../types/filters/insightPackages";

import type { FilterOptionsMap } from "./filters.utils";

export type InsightPackagesFilters = {
    [InsightPackagesFilterField.BRAND]: FilterOptionsMap;
    [InsightPackagesFilterField.BROWSE_NODE]: FilterOptionsMap;
    [InsightPackagesFilterField.OBJECTIVE]: FilterOptionsMap;
};

export const generateInsightPackageFilterOptions = (
    insights: StandaloneRecommendation[],
): InsightPackagesFilters => {
    const filters: InsightPackagesFilters = {
        [InsightPackagesFilterField.BRAND]: {},
        [InsightPackagesFilterField.BROWSE_NODE]: {},
        [InsightPackagesFilterField.OBJECTIVE]: {},
    };

    insights.forEach((insight: StandaloneRecommendation) => {
        const { brand, brandId, browseNode, browseNodeId, objective } = insight;

        if (
            brandId &&
            brand &&
            !has(filters[InsightPackagesFilterField.BRAND], brandId)
        ) {
            filters[InsightPackagesFilterField.BRAND][brandId] = {
                label: brand,
                value: brandId,
            };
        }

        if (
            browseNodeId &&
            browseNode &&
            !has(filters[InsightPackagesFilterField.BROWSE_NODE], browseNodeId)
        ) {
            filters[InsightPackagesFilterField.BROWSE_NODE][browseNodeId] = {
                label: browseNode,
                value: browseNodeId,
            };
        }

        if (
            objective &&
            !has(filters[InsightPackagesFilterField.OBJECTIVE], objective)
        ) {
            filters[InsightPackagesFilterField.OBJECTIVE][objective] = {
                label: objective,
                value: objective,
            };
        }
    });

    return filters;
};

export const generateInsightPackageFilterOptionsForPackageAPI = (
    insights: StandalonePackage[],
): InsightPackagesFilters => {
    const filters: InsightPackagesFilters = {
        [InsightPackagesFilterField.BRAND]: {},
        [InsightPackagesFilterField.BROWSE_NODE]: {},
        [InsightPackagesFilterField.OBJECTIVE]: {},
    };

    insights.forEach((insight: StandalonePackage) => {
        const { brand, brandId, browseNode, browseNodeId, adObjectives } =
            insight;

        const fields = [
            {
                label: brand,
                value: brandId,
                filterField: InsightPackagesFilterField.BRAND,
            },
            {
                label: browseNode,
                value: browseNodeId,
                filterField: InsightPackagesFilterField.BROWSE_NODE,
            },
        ];

        adObjectives?.forEach((objective) => {
            fields.push({
                label: objective,
                value: objective,
                filterField: InsightPackagesFilterField.OBJECTIVE,
            });
        });

        fields.forEach((item) => {
            if (
                item["label"] &&
                item["value"] &&
                !has(filters[item["filterField"]], item["value"])
            ) {
                filters[item["filterField"]][item["value"]] = {
                    label: item["label"],
                    value: item["value"],
                };
            }
        });
    });

    return filters;
};

export const stripPunctuation = (text: string): string => {
    return text.replace(/[.,/#!$%^'&<>*;:{}=\-_`~()]/g, "").trim();
};

export const fieldIncludesSearchQuery = (
    fieldValue: string | undefined,
    searchQuery: string,
): boolean => {
    return (
        !!fieldValue &&
        stripPunctuation(fieldValue)
            .toLowerCase()
            .includes(stripPunctuation(searchQuery.trim().toLowerCase()))
    );
};

export const matchesSearchQuery = (
    insight: StandaloneRecommendation | StandalonePackage,
    searchQuery?: string,
): boolean => {
    return (
        !searchQuery ||
        searchQuery.trim() === "" ||
        fieldIncludesSearchQuery(insight.brand, searchQuery) ||
        fieldIncludesSearchQuery(insight.browseNode, searchQuery) ||
        fieldIncludesSearchQuery(insight.title, searchQuery)
    );
};

export const matchesInsightPackageSelections = (
    insight: StandaloneRecommendation,
    fieldSelections: InsightPackagesFilterSelections,
    searchQuery?: string,
): boolean => {
    // A match is considered true if any of the following conditions are met
    // 1. The insight has that field defined and that field matches the selected option
    // 2. The default option is selected
    // 3. The insight does not have that field

    const matchesBrand =
        (insight.brand &&
            insight.brandId &&
            fieldSelections.PACKAGED_BRAND.some(
                (brand) => brand.value === insight.brandId,
            )) ||
        fieldSelections.PACKAGED_BRAND.length === 0 ||
        isEmpty(insight.brandId) ||
        isEmpty(insight.brand);

    const matchesBrowseNode =
        (insight.browseNode &&
            insight.browseNodeId &&
            fieldSelections.PACKAGED_BROWSE_NODE.some(
                (browseNode) => browseNode.value === insight.browseNodeId,
            )) ||
        fieldSelections.PACKAGED_BROWSE_NODE.length === 0 ||
        isEmpty(insight.browseNodeId) ||
        isEmpty(insight.browseNode);

    const matchesObjective =
        (insight.objective &&
            fieldSelections.PACKAGED_OBJECTIVE.some(
                (objective) => objective.value === insight.objective,
            )) ||
        fieldSelections.PACKAGED_OBJECTIVE.length === 0 ||
        isEmpty(insight.objective);

    return (
        matchesBrand &&
        matchesBrowseNode &&
        matchesObjective &&
        matchesSearchQuery(insight, searchQuery)
    );
};

export const matchesInsightPackageSelectionsForPackageAPI = (
    insight: StandalonePackage,
    fieldSelections: InsightPackagesFilterSelections,
    searchQuery?: string,
): boolean => {
    // A match is considered true if any of the following conditions are met
    // 1. The insight has that field defined and that field matches the selected option
    // 2. The default option is selected
    // 3. The insight does not have that field

    const matchesBrand =
        (insight.brand &&
            insight.brandId &&
            fieldSelections.PACKAGED_BRAND.some(
                (brand) => brand.value === insight.brandId,
            )) ||
        fieldSelections.PACKAGED_BRAND.length === 0 ||
        isEmpty(insight.brandId) ||
        isEmpty(insight.brand);

    const matchesBrowseNode =
        (insight.browseNode &&
            insight.browseNodeId &&
            fieldSelections.PACKAGED_BROWSE_NODE.some(
                (browseNode) => browseNode.value === insight.browseNodeId,
            )) ||
        fieldSelections.PACKAGED_BROWSE_NODE.length === 0 ||
        isEmpty(insight.browseNodeId) ||
        isEmpty(insight.browseNode);

    const matchesObjectives =
        (insight.adObjectives &&
            fieldSelections.PACKAGED_OBJECTIVE.length > 0 &&
            fieldSelections.PACKAGED_OBJECTIVE.some((objective) =>
                insight.adObjectives?.includes(objective?.value.toString()),
            )) ||
        fieldSelections.PACKAGED_OBJECTIVE.length === 0 ||
        isEmpty(insight.adObjectives);

    return (
        matchesBrand &&
        matchesBrowseNode &&
        matchesObjectives &&
        matchesSearchQuery(insight, searchQuery)
    );
};
